import { useLingui } from '@lingui/react/macro'
import dayjs from 'dayjs'
import { usePlotBounds } from 'features/timeSeries/components/chartOverlays/usePlotBounds'
import { useXScaleRedraw } from 'features/timeSeries/components/chartOverlays/useXScaleRedraw'
import { projectLabelSpan } from 'features/timeSeries/components/labelTool/labelGeometry'
import { X } from 'lucide-react'
import React, { useRef } from 'react'
import styled from 'styled-components'
import uPlot from 'uplot'

import { dismissBand, useAgentViewState } from './agentView'

/**
 * The span the agent asked the reader to look at.
 *
 * DOM rather than canvas, unlike the model's train/test tints: this one carries
 * a close button, and a hit target has to be an element. Violet, against the
 * ruler's blue and a label's amber — the three markup layers draw the same
 * full-height band, so hue is what tells them apart at a glance.
 */

const ACCENT = '#a78bfa'
const FILL = 'rgba(167, 139, 250, 0.12)'

const OverlayRoot = styled.div`
  position: absolute;
  inset: 0;
  z-index: 15;
  pointer-events: none;
  user-select: none;
`

const Band = styled.div`
  position: absolute;
  background: ${FILL};
  border-left: 1px solid ${ACCENT};
  border-right: 1px solid ${ACCENT};
  pointer-events: none;
`

const Caption = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 2px 0 5px;
  border-radius: 2px;
  background: ${ACCENT};
  color: #0d0d0d;
  font-family: 'Manrope', sans-serif;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  pointer-events: all;
`

const Close = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`

interface AgentBandOverlayProps {
  uPlotRef: React.RefObject<uPlot | null>
}

export const AgentBandOverlay: React.FC<AgentBandOverlayProps> = ({
  uPlotRef,
}) => {
  const { t } = useLingui()
  const { band } = useAgentViewState()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const plotBounds = usePlotBounds(uPlotRef, rootRef)
  useXScaleRedraw(uPlotRef)

  if (!band) return null

  // The agent bands whole days, so an end of "2025-03-04" means through that
  // day — taken literally it would be a zero-width band on a one-day span.
  const from = dayjs(band.start).startOf('day')
  const to = dayjs(band.end).endOf('day')
  if (!from.isValid() || !to.isValid() || !to.isAfter(from)) return null

  const u = uPlotRef.current
  const valToPos =
    u && plotBounds
      ? (value: number, scale: string) => u.valToPos(value, scale)
      : null
  const span = projectLabelSpan(
    { startMs: from.valueOf(), endMs: to.valueOf() },
    valToPos,
    plotBounds
  )
  if (!span.horizontallyVisible) return null

  const plotTop = plotBounds?.overTop ?? 0
  const plotBottom = plotBounds
    ? `calc(100% - ${plotBounds.overBottom}px)`
    : '0'

  return (
    <OverlayRoot ref={rootRef} data-testid='agent-band-overlay'>
      <Band
        data-testid='agent-band'
        style={{
          left: span.startPx,
          width: Math.max(0, span.endPx - span.startPx),
          top: plotTop,
          bottom: plotBottom,
        }}
      />
      <Caption style={{ left: span.startPx + 2, top: plotTop + 2 }}>
        {band.label || 'agent'}
        <Close
          type='button'
          aria-label={t`Dismiss the highlighted range`}
          onClick={dismissBand}>
          <X size={10} />
        </Close>
      </Caption>
    </OverlayRoot>
  )
}
